import { computed, ref } from 'vue';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

const STORAGE_KEY = 'flight-track-theme';

export type ThemePreference = 'light' | 'dark' | 'system';

const themePreference = ref<ThemePreference>('system');
const isDarkRef = ref(false);

function getStoredTheme(): ThemePreference {
  if (typeof localStorage === 'undefined') return 'system';
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  return 'system';
}

async function syncStatusBar(dark: boolean): Promise<void> {
  if (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform()) {
    try {
      await StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light });
      await StatusBar.setBackgroundColor({ color: dark ? '#020617' : '#ffffff' });
    } catch {
      // StatusBar may not be available
    }
  }
}

function applyToDocument(pref: ThemePreference): void {
  const dark =
    pref === 'dark' ||
    (pref === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  if (typeof document !== 'undefined') {
    document.documentElement.classList.toggle('ion-palette-dark', dark);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', dark ? '#020617' : '#ffffff');
  }
  isDarkRef.value = dark;
  syncStatusBar(dark);
}

let systemListener: (() => void) | null = null;

function applyTheme(pref: ThemePreference): void {
  if (systemListener) {
    window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', systemListener);
    systemListener = null;
  }
  if (pref === 'system' && typeof window !== 'undefined') {
    systemListener = () => applyToDocument('system');
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', systemListener);
  }
  applyToDocument(pref);
}

/**
 * Initialize theme from storage and apply to document. Call once before app mount (e.g. in main.ts) to avoid flash.
 */
export function initTheme(): void {
  const pref = getStoredTheme();
  themePreference.value = pref;
  applyTheme(pref);
}

/**
 * Returns whether the effective theme is dark (for status bar, etc.).
 */
export function getIsDark(): boolean {
  return isDarkRef.value;
}

export function useTheme() {
  const theme = themePreference;
  const isDark = computed(() => isDarkRef.value);

  function setTheme(pref: ThemePreference): void {
    themePreference.value = pref;
    if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, pref);
    applyTheme(pref);
  }

  return { theme, setTheme, isDark };
}
